// features/projects/templates/continuum/record.js
// ═══════════════════════════════════════════════════════════
// CONTINUUM RECORD — parent tab for the methodology registers
//
// Consolidates the three register tabs (Hypotheses · Predictions
// · Questions) under a single parent. They share a register-shaped
// pattern (status pills, lifecycle, claim + caveat) so navigating
// between them is a sub-tab switch, not a top-level page switch.
//
// Per the Continuum 8-tab reorg (Option B, locked 2026-05-03 with
// Tim). The three sub-views are unchanged — Record is purely an
// IA shell that adds a sub-tab nav strip and routes to the existing
// components.
//
// The sub-component's own header (pill label + counts + Refresh)
// is preserved; Record's only contribution is the nav strip above
// it. No layout overlap.
// ═══════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import { C, FONT as F } from './tokens';
import ContinuumHypotheses from './hypotheses';
import ContinuumPredictions from './predictions';
import ContinuumQuestions from './questions';

const SUB_TABS = [
  { key: 'hypotheses',  label: 'Hypotheses',  color: C.teal,
    description: 'Parallel claims that bind cross-domain evidence' },
  { key: 'predictions', label: 'Predictions', color: C.sage,
    description: 'Forward predictions resolving on dated criteria' },
  { key: 'questions',   label: 'Questions',   color: C.amber,
    description: 'Open unknowns surfaced during research' },
];

export default function ContinuumRecord({ workspaceId, userId }) {
  // Sub-tab persistence — keyed by workspace so each Continuum
  // project remembers its own last-active sub-tab.
  const storageKey = `continuum_record_subtab_${workspaceId}`;
  const [activeSubTab, setActiveSubTab] = useState(() => {
    if (typeof window === 'undefined') return 'hypotheses';
    return window.localStorage.getItem(storageKey) || 'hypotheses';
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(storageKey, activeSubTab);
  }, [storageKey, activeSubTab]);

  return (
    <div style={{
      height: '100%', background: C.bg, color: C.text,
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
    }}>
      <SubTabNav
        active={activeSubTab}
        onSelect={setActiveSubTab}
      />
      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        {activeSubTab === 'hypotheses' && (
          <ContinuumHypotheses workspaceId={workspaceId} userId={userId} />
        )}
        {activeSubTab === 'predictions' && (
          <ContinuumPredictions workspaceId={workspaceId} userId={userId} />
        )}
        {activeSubTab === 'questions' && (
          <ContinuumQuestions workspaceId={workspaceId} userId={userId} />
        )}
      </div>
    </div>
  );
}

function SubTabNav({ active, onSelect }) {
  const activeMeta = SUB_TABS.find(t => t.key === active);
  return (
    <div style={{
      display: 'flex', alignItems: 'center', padding: '0 20px', height: 36,
      borderBottom: `1px solid ${C.b1}`, background: C.s1, flexShrink: 0, gap: 14,
    }}>
      <div style={{
        fontFamily: F.mono, fontSize: 8, letterSpacing: '0.16em',
        color: C.dim, textTransform: 'uppercase', fontWeight: 600,
      }}>
        ◆ Record
      </div>
      <div style={{ width: 1, height: 14, background: C.b2 }} />
      <div style={{ display: 'flex', gap: 4 }}>
        {SUB_TABS.map(t => {
          const isActive = active === t.key;
          return (
            <button
              key={t.key}
              onClick={() => onSelect(t.key)}
              title={t.description}
              style={{
                fontSize: 9,
                fontFamily: F.mono,
                letterSpacing: '0.06em',
                padding: '4px 12px',
                borderRadius: 3,
                border: `1px solid ${isActive ? t.color : 'transparent'}`,
                background: isActive ? hexToBg(t.color, 0.10) : 'transparent',
                color: isActive ? t.color : C.static,
                cursor: 'pointer',
                transition: 'all 0.12s',
                textTransform: 'uppercase',
              }}
              onMouseEnter={(e) => {
                if (!isActive) e.currentTarget.style.color = C.mid;
              }}
              onMouseLeave={(e) => {
                if (!isActive) e.currentTarget.style.color = C.static;
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>
      {activeMeta && (
        <div style={{
          marginLeft: 'auto',
          fontFamily: F.sans, fontSize: 10, color: C.dim, fontStyle: 'italic',
        }}>
          {activeMeta.description}
        </div>
      )}
    </div>
  );
}

function hexToBg(hex, alpha) {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}
